/**
 * Header Navigation - JavaScript Functionality
 * Includes: News Marquee Animation + Header Mega Menu Dropdown + Sticky Header on Scroll
 */

// Hero Logo Scroll Observer - Show header logo when hero logo scrolls out of view
(() => {
    const initHeroLogoObserver = () => {
        // Skip on header navigation editor page
        const header = document.querySelector("[data-header-nav-editor]");
        if (header) return;

        const heroLogo = document.querySelector(".hero-section .hero-logo");
        if (!heroLogo) return; // Only run on pages with hero

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        // Hero logo visible - hide header logo
                        document.body.classList.remove("hero-logo-scrolled");
                    } else {
                        // Hero logo not visible - show header logo
                        document.body.classList.add("hero-logo-scrolled");
                    }
                });
            },
            {
                threshold: 0,
                // Negative top margin: header logo appears only after hero logo is fully scrolled out
                // Account for sticky header height (~100px)
                rootMargin: "-100px 0px 0px 0px",
            },
        );

        observer.observe(heroLogo);
    };

    // Initialize when DOM is ready
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", initHeroLogoObserver);
    } else {
        initHeroLogoObserver();
    }
})();

// Sticky Header - Show on scroll up, hide on scroll down
(() => {
    const initStickyHeader = () => {
        const header = document.querySelector(".site-header");

        // Need at least the header to proceed
        if (!header) return;

        // Skip sticky behavior on header navigation editor page
        if (header.hasAttribute("data-header-nav-editor")) return;

        const newsbar = document.querySelector(".news-marquee");

        let lastScrollY = window.scrollY;
        let ticking = false;
        let totalHeight = 0;
        let isHidden = false;
        const scrollThreshold = 10;

        const updateHeights = () => {
            const newsbarHeight = newsbar ? newsbar.offsetHeight : 0;
            const headerHeight = header.offsetHeight;
            totalHeight = newsbarHeight + headerHeight;

            document.documentElement.style.setProperty("--newsbar-height", `${newsbarHeight}px`);
            document.documentElement.style.setProperty("--header-height", `${headerHeight}px`);
            document.documentElement.style.setProperty("--header-total-height", `${totalHeight}px`);
        };

        updateHeights();

        // Add sticky-enabled class to body for CSS targeting
        document.body.classList.add("sticky-header-enabled");

        const onScroll = () => {
            const currentScrollY = window.scrollY;
            const scrollDiff = currentScrollY - lastScrollY;

            // Near the top, reset to default (non-compact) header
            if (currentScrollY <= 50) {
                document.body.classList.remove("header-hidden");
                document.body.classList.remove("header-sticky");
                isHidden = false;
                lastScrollY = currentScrollY;
                ticking = false;
                return;
            }

            // Only act after scrolling past threshold
            if (Math.abs(scrollDiff) < scrollThreshold) {
                ticking = false;
                return;
            }

            // Scrolling down - hide header
            if (scrollDiff > 0 && currentScrollY > totalHeight) {
                if (!isHidden && !header.classList.contains("menu-open")) {
                    document.body.classList.add("header-hidden");
                    document.body.classList.add("header-sticky");
                    isHidden = true;
                }
            }
            // Scrolling up - show header (sticky)
            else if (scrollDiff < 0) {
                if (isHidden) {
                    document.body.classList.remove("header-hidden");
                    isHidden = false;
                }
                if (currentScrollY > 0) {
                    document.body.classList.add("header-sticky");
                }
            }

            lastScrollY = currentScrollY;
            ticking = false;
        };

        const onScrollThrottled = () => {
            if (!ticking) {
                requestAnimationFrame(onScroll);
                ticking = true;
            }
        };

        window.addEventListener("scroll", onScrollThrottled, { passive: true });
        window.addEventListener("resize", updateHeights);

        window.addEventListener("pageshow", (e) => {
            if (e.persisted) {
                lastScrollY = window.scrollY;
                isHidden = false;
                document.body.classList.remove("header-hidden");
                if (window.scrollY <= 50) {
                    document.body.classList.remove("header-sticky");
                }
                window.addEventListener("scroll", onScrollThrottled, { passive: true });
                window.addEventListener("resize", updateHeights);
            }
        });

        window.addEventListener("pagehide", () => {
            window.removeEventListener("scroll", onScrollThrottled);
            window.removeEventListener("resize", updateHeights);
        });
    };

    // Initialize when DOM is ready
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", initStickyHeader);
    } else {
        initStickyHeader();
    }
})();

// News Marquee Animation - Pure JavaScript (no Alpine dependency)
(() => {
    const initMarquee = () => {
        // Skip marquee on header navigation editor page
        const header = document.querySelector("[data-header-nav-editor]");
        if (header) return;

        const tracks = document.querySelectorAll(".marquee-track");

        tracks.forEach((track) => {
            let position = 0;
            const speed = 1;
            const marquee = track.closest(".news-marquee");
            let isPaused = false;

            if (!marquee) return;

            // Pause on hover
            marquee.addEventListener("mouseenter", () => {
                isPaused = true;
            });

            marquee.addEventListener("mouseleave", () => {
                isPaused = false;
            });

            const loop = () => {
                if (!isPaused) {
                    position -= speed;
                    if (Math.abs(position) >= track.scrollWidth / 2) {
                        position = 0;
                    }
                    track.style.transform = `translateX(${position}px)`;
                }
                requestAnimationFrame(loop);
            };

            requestAnimationFrame(loop);
        });
    };

    // Initialize when DOM is ready
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", initMarquee);
    } else {
        initMarquee();
    }
})();

// Language Switcher Functionality
(() => {
    const initLanguageSwitcher = () => {
        const switchers = document.querySelectorAll("[data-language-switcher]");

        switchers.forEach((switcher) => {
            const trigger = switcher.querySelector(".language-trigger");
            const dropdown = switcher.querySelector(".language-dropdown");
            const options = switcher.querySelectorAll(".language-option");

            if (!trigger || !dropdown) return;

            // Toggle dropdown
            trigger.addEventListener("click", (e) => {
                e.stopPropagation();
                const isOpen = switcher.classList.contains("is-open");

                // Close all other switchers
                document.querySelectorAll("[data-language-switcher]").forEach((s) => {
                    s.classList.remove("is-open");
                    const t = s.querySelector(".language-trigger");
                    if (t) t.setAttribute("aria-expanded", "false");
                });

                if (!isOpen) {
                    switcher.classList.add("is-open");
                    trigger.setAttribute("aria-expanded", "true");
                    document.body.style.overflow = "hidden";
                } else {
                    document.body.style.overflow = "";
                }
            });

            // Handle option selection - navigate for <a> tags
            options.forEach((option) => {
                option.addEventListener("click", () => {
                    switcher.classList.remove("is-open");
                    trigger.setAttribute("aria-expanded", "false");
                    document.body.style.overflow = "";

                    // If option is a link (<a> tag), navigate to href
                    const href = option.getAttribute("href");
                    if (href && option.tagName.toLowerCase() === "a") {
                        // Use location.href for full page reload (language change)
                        window.location.href = href;
                    }
                });
            });

            // Close on outside click
            document.addEventListener("click", (e) => {
                if (!switcher.contains(e.target)) {
                    switcher.classList.remove("is-open");
                    trigger.setAttribute("aria-expanded", "false");
                    document.body.style.overflow = "";
                }
            });

            // Close on escape
            document.addEventListener("keydown", (e) => {
                if (e.key === "Escape") {
                    switcher.classList.remove("is-open");
                    trigger.setAttribute("aria-expanded", "false");
                    document.body.style.overflow = "";
                }
            });
        });
    };

    // Initialize when DOM is ready
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", initLanguageSwitcher);
    } else {
        initLanguageSwitcher();
    }
})();

// Footer Language Switcher Functionality
(() => {
    const initFooterLanguageSwitcher = () => {
        const switchers = document.querySelectorAll("[data-footer-language-switcher]");

        switchers.forEach((switcher) => {
            const trigger = switcher.querySelector(".footer-language-trigger");
            const dropdown = switcher.querySelector(".footer-language-dropdown");
            const options = switcher.querySelectorAll(".footer-language-option");

            if (!trigger || !dropdown) return;

            // Toggle dropdown
            trigger.addEventListener("click", (e) => {
                e.stopPropagation();
                const isOpen = switcher.classList.contains("is-open");

                // Close all other switchers (header and footer)
                document.querySelectorAll("[data-language-switcher], [data-footer-language-switcher]").forEach((s) => {
                    s.classList.remove("is-open");
                    const t = s.querySelector(".language-trigger, .footer-language-trigger");
                    if (t) t.setAttribute("aria-expanded", "false");
                });

                if (!isOpen) {
                    switcher.classList.add("is-open");
                    trigger.setAttribute("aria-expanded", "true");
                }
            });

            // Handle option selection - navigate for <a> tags
            options.forEach((option) => {
                option.addEventListener("click", () => {
                    switcher.classList.remove("is-open");
                    trigger.setAttribute("aria-expanded", "false");

                    // If option is a link (<a> tag), navigate to href
                    const href = option.getAttribute("href");
                    if (href && option.tagName.toLowerCase() === "a") {
                        // Use location.href for full page reload (language change)
                        window.location.href = href;
                    }
                });
            });

            // Close on outside click
            document.addEventListener("click", (e) => {
                if (!switcher.contains(e.target)) {
                    switcher.classList.remove("is-open");
                    trigger.setAttribute("aria-expanded", "false");
                }
            });

            // Close on escape
            document.addEventListener("keydown", (e) => {
                if (e.key === "Escape") {
                    switcher.classList.remove("is-open");
                    trigger.setAttribute("aria-expanded", "false");
                }
            });
        });
    };

    // Initialize when DOM is ready
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", initFooterLanguageSwitcher);
    } else {
        initFooterLanguageSwitcher();
    }
})();

// Header Mega Menu Functionality
(() => {
    const initMegaMenu = () => {
        document.querySelectorAll("[data-header]").forEach((header) => {
            // Detect if we're editing the Header Navigation page in Neos backend
            // This attribute is only set when on the HeaderNavigation document in backend
            const isHeaderNavEditor = header.hasAttribute("data-header-nav-editor");

            // Detect if we're in Neos backend (any page) - check for neos in URL or body class
            const isNeosBackend =
                window.location.href.indexOf("/neos/") !== -1 ||
                document.body.classList.contains("neos-backend") ||
                document.querySelector(".neos-application");

            // Use click behavior in backend (except on header nav editor page where menus are stacked)
            const useClickBehavior = isNeosBackend && !isHeaderNavEditor;

            const megaMenus = header.querySelectorAll("[data-mega-menu]");
            const menuTriggers = header.querySelectorAll("[data-menu-trigger]");
            const navItemsContainer = header.querySelector(".nav-items");
            let closeTimeout = null;
            let closeAnimationTimeout = null;
            let currentMenuIndex = null;
            let isOverMenu = false;
            let isOverNavArea = false;
            let isClosing = false;

            if (!navItemsContainer) return;

            // Frontend only (not backend): Track hover on entire nav area
            if (!isHeaderNavEditor && !useClickBehavior) {
                navItemsContainer.addEventListener("mouseenter", () => {
                    isOverNavArea = true;
                    cancelClose();
                });

                navItemsContainer.addEventListener("mouseleave", () => {
                    isOverNavArea = false;
                    scheduleClose();
                });
            }

            // Skip all menu interactions on header nav editor page (menus are always visible)
            if (isHeaderNavEditor) return;

            // Handle individual nav item events
            menuTriggers.forEach((trigger) => {
                if (useClickBehavior) {
                    // Backend (regular pages): click to toggle
                    trigger.addEventListener("click", (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const menuIndex = parseInt(trigger.getAttribute("data-menu-trigger"), 10);

                        // Toggle - if same menu is open, close it
                        if (currentMenuIndex === menuIndex && !isClosing) {
                            closeMenu();
                        } else {
                            cancelClose();
                            openMenu(menuIndex);
                        }
                    });
                } else {
                    // Frontend: hover to open
                    trigger.addEventListener("mouseenter", () => {
                        cancelClose();
                        const menuIndex = trigger.getAttribute("data-menu-trigger");
                        openMenu(parseInt(menuIndex, 10));
                    });
                }
            });

            // Frontend only: Handle mega menu container hover
            const menuContainer = header.querySelector("[data-mega-menu-container]");
            if (menuContainer && !isHeaderNavEditor && !useClickBehavior) {
                menuContainer.addEventListener("mouseenter", () => {
                    isOverMenu = true;
                    cancelClose();
                });

                menuContainer.addEventListener("mouseleave", () => {
                    isOverMenu = false;
                    scheduleClose();
                });
            }

            // Backend click behavior: close menu when clicking outside
            if (useClickBehavior) {
                document.addEventListener("click", (e) => {
                    if (!header.contains(e.target)) {
                        closeMenu();
                    }
                });
            }

            const scheduleClose = () => {
                // Backend: don't auto-close
                if (isHeaderNavEditor) return;

                cancelClose();
                closeTimeout = setTimeout(() => {
                    if (!isOverNavArea && !isOverMenu) {
                        closeMenu();
                    }
                }, 300);
            };

            const cancelClose = () => {
                // Cancel scheduled close
                if (closeTimeout) {
                    clearTimeout(closeTimeout);
                    closeTimeout = null;
                }
                // Cancel any ongoing close animation
                if (closeAnimationTimeout) {
                    clearTimeout(closeAnimationTimeout);
                    closeAnimationTimeout = null;
                }
                // If we were closing, reset the state
                if (isClosing) {
                    isClosing = false;
                    header.classList.remove("menu-closing");
                }
            };

            const openMenu = (index) => {
                cancelClose();

                // If same menu, do nothing (except in backend where we handle toggle separately)
                if (currentMenuIndex === index && !isClosing && !isHeaderNavEditor) return;

                const targetMenu = header.querySelector(`[data-mega-menu="${index}"]`);

                if (targetMenu) {
                    // Remove active class from all nav links
                    menuTriggers.forEach((t) => {
                        t.classList.remove("is-active");
                    });

                    // Add active class to current nav link (find the trigger with matching data-menu-trigger)
                    const activeTrigger = header.querySelector(`[data-menu-trigger="${index}"]`);
                    if (activeTrigger) {
                        activeTrigger.classList.add("is-active");
                    }

                    // Close all menus first
                    megaMenus.forEach((menu) => {
                        menu.classList.remove("is-open");
                    });

                    // Open target menu and header
                    header.classList.remove("menu-closing");
                    header.classList.add("menu-open");
                    targetMenu.classList.add("is-open");
                    currentMenuIndex = index;
                    isClosing = false;

                    // Lock body scroll when menu is open (frontend only)
                    if (!isHeaderNavEditor) {
                        document.body.style.overflow = "hidden";
                    }
                } else {
                    // No submenu for this item - close any open menu
                    closeMenu();
                }
            };

            const closeMenu = () => {
                // Don't close if already closing
                if (isClosing) return;

                isClosing = true;
                header.classList.add("menu-closing");

                // Unlock body scroll (frontend only)
                if (!isHeaderNavEditor) {
                    document.body.style.overflow = "";
                }

                menuTriggers.forEach((t) => {
                    t.classList.remove("is-active");
                });

                // Reset search icon to magnifier
                const searchBtn = header.querySelector("[data-search-toggle]");
                if (searchBtn) searchBtn.classList.remove("is-search-active");

                // Use a single timeout for the full close animation
                closeAnimationTimeout = setTimeout(() => {
                    // Only complete close if we're still in closing state
                    if (isClosing) {
                        header.classList.remove("menu-open");
                        header.classList.remove("menu-closing");
                        megaMenus.forEach((menu) => {
                            menu.classList.remove("is-open");
                        });
                        currentMenuIndex = null;
                        isClosing = false;
                    }
                }, 280);
            };

            // Close on escape
            document.addEventListener("keydown", (e) => {
                if (e.key === "Escape") {
                    isOverNavArea = false;
                    isOverMenu = false;
                    cancelClose();
                    closeMenu();
                }
            });
        });
    };

    // Initialize when DOM is ready
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", initMegaMenu);
    } else {
        initMegaMenu();
    }
})();

// Mobile Menu Toggle
(() => {
    const initMobileMenu = () => {
        const header = document.querySelector(".site-header");
        const mobileToggle = document.querySelector("[data-mobile-menu-toggle]");
        const mobileOverlay = document.querySelector("[data-mobile-menu]");
        const closeButtons = document.querySelectorAll("[data-mobile-menu-close]");

        if (!header || !mobileToggle) return;

        const openMenu = () => {
            header.classList.add("mobile-menu-open");
            document.body.style.overflow = "hidden";
        };

        const closeMenu = () => {
            header.classList.remove("mobile-menu-open");
            header.classList.remove("mobile-submenu-open");
            document.body.style.overflow = "";
            // Close all sub-menus when main menu closes
            document.querySelectorAll("[data-mobile-submenu]").forEach((submenu) => {
                submenu.classList.remove("is-open");
                submenu.classList.add("invisible");
                submenu.style.transform = "translateX(100%)";
            });
        };

        // Toggle mobile menu (hamburger button)
        mobileToggle.addEventListener("click", () => {
            // If search is open on mobile, close it instead
            if (header.classList.contains("menu-open") && window.__headerSearch) {
                window.__headerSearch.close();
                return;
            }
            if (header.classList.contains("mobile-menu-open")) {
                closeMenu();
            } else {
                openMenu();
            }
        });

        // Close buttons (X icons in overlay and sub-menus)
        closeButtons.forEach((btn) => {
            btn.addEventListener("click", closeMenu);
        });

        // Sub-menu triggers - slide in sub-menu
        const navTriggers = document.querySelectorAll("[data-mobile-nav-trigger]");
        navTriggers.forEach((trigger) => {
            trigger.addEventListener("click", () => {
                const menuIndex = trigger.getAttribute("data-mobile-nav-trigger");
                const submenu = document.querySelector(`[data-mobile-submenu="${menuIndex}"]`);
                if (submenu) {
                    // Reset scroll position to top when opening
                    const content = submenu.querySelector(".mobile-submenu-content");
                    if (content) content.scrollTop = 0;
                    submenu.classList.remove("invisible");
                    submenu.classList.add("is-open");
                    submenu.style.transform = "translateX(0)";
                    // Add class for white header
                    header.classList.add("mobile-submenu-open");
                }
            });
        });

        // Back buttons in sub-menus
        const backButtons = document.querySelectorAll("[data-mobile-submenu-back]");
        backButtons.forEach((btn) => {
            btn.addEventListener("click", () => {
                const submenu = btn.closest("[data-mobile-submenu]");
                if (submenu) {
                    submenu.classList.remove("is-open");
                    submenu.classList.remove("is-scrolled");
                    submenu.style.transform = "translateX(100%)";
                    // Remove white header class
                    header.classList.remove("mobile-submenu-open");
                    // Reset scroll position after animation
                    setTimeout(() => {
                        if (!submenu.classList.contains("is-open")) {
                            submenu.classList.add("invisible");
                            const content = submenu.querySelector(".mobile-submenu-content");
                            if (content) content.scrollTop = 0;
                        }
                    }, 300);
                }
            });
        });

        // Swipe right to close submenu (mimics back button)
        const SWIPE_THRESHOLD = 50;
        document.querySelectorAll("[data-mobile-submenu]").forEach((submenu) => {
            let touchStartX = 0;
            let touchStartY = 0;

            submenu.addEventListener("touchstart", (e) => {
                touchStartX = e.touches[0].clientX;
                touchStartY = e.touches[0].clientY;
            }, { passive: true });

            submenu.addEventListener("touchend", (e) => {
                if (!submenu.classList.contains("is-open")) return;
                const deltaX = e.changedTouches[0].clientX - touchStartX;
                const deltaY = Math.abs(e.changedTouches[0].clientY - touchStartY);
                // Swipe right with more horizontal than vertical movement
                if (deltaX > SWIPE_THRESHOLD && deltaX > deltaY) {
                    submenu.classList.remove("is-open");
                    submenu.classList.remove("is-scrolled");
                    submenu.style.transform = "translateX(100%)";
                    header.classList.remove("mobile-submenu-open");
                    setTimeout(() => {
                        if (!submenu.classList.contains("is-open")) {
                            submenu.classList.add("invisible");
                            const content = submenu.querySelector(".mobile-submenu-content");
                            if (content) content.scrollTop = 0;
                        }
                    }, 300);
                }
            }, { passive: true });
        });

        // Submenu content scroll detection - show border when scrolled
        const submenuContents = document.querySelectorAll(".mobile-submenu-content");
        submenuContents.forEach((content) => {
            content.addEventListener("scroll", () => {
                const submenu = content.closest("[data-mobile-submenu]");
                if (submenu) {
                    if (content.scrollTop > 0) {
                        submenu.classList.add("is-scrolled");
                    } else {
                        submenu.classList.remove("is-scrolled");
                    }
                }
            });
        });

        // Close mobile menu on escape
        document.addEventListener("keydown", (e) => {
            if (e.key === "Escape" && header.classList.contains("mobile-menu-open")) {
                closeMenu();
            }
        });

        // Close mobile menu when investor target modal opens (user clicked to change investor type)
        window.addEventListener("investorTarget", () => {
            // Small delay to let modal open, then close mobile menu
            setTimeout(() => {
                closeMenu();
            }, 100);
        });
    };

    // Initialize when DOM is ready
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", initMobileMenu);
    } else {
        initMobileMenu();
    }
})();

// Backend: Stacked menus are always visible via CSS, no special JS needed

// Stock Ticker Exchange Switcher (for mega menu stock cards)
(() => {
    /**
     * Animate a number from old to new value with counting effect
     */
    function animateNumber(el, newValue, decimals, duration, prefix, suffix) {
        if (!el) return;
        prefix = prefix || '';
        suffix = suffix || '';
        var startValue = parseFloat(el.textContent.replace(/[^0-9.\-]/g, "")) || 0;
        var endValue = parseFloat(newValue) || 0;
        if (startValue === endValue) {
            el.textContent = prefix + newValue + suffix;
            return;
        }
        var startTime = null;
        function step(timestamp) {
            if (!startTime) startTime = timestamp;
            var progress = Math.min((timestamp - startTime) / duration, 1);
            var eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
            var current = startValue + (endValue - startValue) * eased;
            el.textContent = prefix + current.toFixed(decimals) + suffix;
            if (progress < 1) {
                requestAnimationFrame(step);
            } else {
                el.textContent = prefix + newValue + suffix;
            }
        }
        requestAnimationFrame(step);
    }

    const initStockExchangeSwitcher = () => {
        const stockCards = document.querySelectorAll(".menu-card--stock-ticker");

        stockCards.forEach((card) => {
            const exchangeButtons = card.querySelectorAll(".stock-exchange-link");
            const tickerSymbol = card.querySelector(".stock-ticker-symbol");
            const priceEl = card.querySelector(".stock-price");
            const currencyEl = card.querySelector(".stock-currency");
            const navPriceEl = card.querySelector(".nav-price");
            const navCurrencyEl = card.querySelector(".nav-currency");
            const navDiscountEl = card.querySelector(".nav-discount");
            const discountLabel = card.querySelector(".stock-discount-label");
            const timestamp = card.querySelector(".stock-timestamp");

            exchangeButtons.forEach((button) => {
                button.addEventListener("click", (e) => {
                    e.preventDefault();

                    // Guard against double-click and clicking active button
                    if (card.classList.contains("is-switching") || button.classList.contains("stock-exchange-link--active")) return;

                    // Update active state immediately
                    exchangeButtons.forEach((btn) => {
                        btn.classList.remove("stock-exchange-link--active", "text-inverse-strong", "underline");
                        btn.classList.add("text-inverse-weak", "no-underline");
                    });
                    button.classList.add("stock-exchange-link--active", "text-inverse-strong", "underline");
                    button.classList.remove("text-inverse-weak", "no-underline");

                    // Get data from button attributes
                    const ticker = button.getAttribute("data-ticker");
                    const price = button.getAttribute("data-price");
                    const currency = button.getAttribute("data-currency");
                    const lastUpdateFormatted = button.getAttribute("data-last-update-formatted");
                    const navFormatted = button.getAttribute("data-nav-formatted");
                    const navDiscount = button.getAttribute("data-nav-discount");
                    const navPositive = button.getAttribute("data-nav-positive") === "true";

                    // Slide out - add class to trigger CSS transitions
                    card.classList.add("is-switching");

                    // After slide-out transition (220ms), update data and slide back in
                    setTimeout(() => {
                        // Update ticker symbol
                        if (tickerSymbol) tickerSymbol.textContent = ticker;

                        // Update currencies
                        if (currencyEl) currencyEl.textContent = currency;
                        if (navCurrencyEl) navCurrencyEl.textContent = currency;

                        // Update NAV discount display (green for positive, white for negative per Figma)
                        if (navDiscountEl) {
                            navDiscountEl.textContent = `${navPositive ? "+" : ""}${navDiscount}%`;
                            navDiscountEl.classList.remove("text-success", "text-inverse-strong");
                            navDiscountEl.classList.add(navPositive ? "text-success" : "text-inverse-strong");
                        }

                        if (discountLabel) {
                            discountLabel.textContent = navPositive ? "Premium to NAV" : "Discount to NAV";
                        }

                        if (timestamp && lastUpdateFormatted) {
                            // Get the label text before the date (everything before the first space-separated date)
                            const currentText = timestamp.textContent;
                            const labelMatch = currentText.match(/^(.+?\s)(\d)/);
                            const label = labelMatch ? labelMatch[1] : "Closing prices as of ";
                            timestamp.textContent = label + lastUpdateFormatted;
                        }

                        // Slide back in - remove class to reverse transition
                        card.classList.remove("is-switching");

                        // Animate numbers after slide-in starts
                        animateNumber(priceEl, price, 2, 400);
                        animateNumber(navPriceEl, navFormatted, 2, 400);

                        // Animate NAV discount (with prefix and % suffix preserved)
                        if (navDiscountEl) {
                            var prefix = navPositive ? "+" : "";
                            animateNumber(navDiscountEl, navDiscount, 1, 300, prefix, "%");
                        }
                    }, 220);
                });
            });
        });
    };

    // Initialize when DOM is ready
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", initStockExchangeSwitcher);
    } else {
        initStockExchangeSwitcher();
    }
})();

// Search Button - Integrates with Mega Menu system
// The search is rendered as data-mega-menu="search" inside the mega-menu-container.
// This handler opens/closes it using the same menu-open/is-open mechanism.
(() => {
    const initSearchButton = () => {
        const header = document.querySelector("[data-header]");
        const searchToggle = document.querySelector("[data-search-toggle]");

        if (!header || !searchToggle) return;

        // Skip on header nav editor page
        if (header.hasAttribute("data-header-nav-editor")) return;

        const megaMenus = header.querySelectorAll("[data-mega-menu]");
        const menuTriggers = header.querySelectorAll("[data-menu-trigger]");
        const searchMenu = header.querySelector('[data-mega-menu="search"]');

        if (!searchMenu) return;

        let closeTimeout = null;
        let closeAnimationTimeout = null;
        let currentMenuIndex = null;
        let isClosing = false;

        // Reuse references from the mega menu scope by reading DOM state
        const isSearchOpen = () => searchMenu.classList.contains("is-open");

        const openSearchMenu = () => {
            if (isSearchOpen() && !isClosing) return;

            // Cancel any pending close
            if (closeTimeout) { clearTimeout(closeTimeout); closeTimeout = null; }
            if (closeAnimationTimeout) { clearTimeout(closeAnimationTimeout); closeAnimationTimeout = null; }
            if (isClosing) { isClosing = false; header.classList.remove("menu-closing"); }

            // Remove active state from nav triggers
            menuTriggers.forEach((t) => t.classList.remove("is-active"));

            // Close all menus first
            megaMenus.forEach((menu) => menu.classList.remove("is-open"));

            // Open search menu + header menu-open state
            header.classList.remove("menu-closing");
            header.classList.add("menu-open");
            searchMenu.classList.add("is-open");
            searchToggle.classList.add("is-search-active");

            document.body.style.overflow = "hidden";

            // Focus input immediately to trigger mobile keyboard,
            // then re-focus after animation for proper scroll position
            const input = searchMenu.querySelector(".header-search-input");
            if (input) {
                input.focus({ preventScroll: true });
                setTimeout(() => input.focus(), 350);
            }
        };

        const closeSearchMenu = () => {
            if (!isSearchOpen()) return;
            if (isClosing) return;

            isClosing = true;
            header.classList.add("menu-closing");
            searchToggle.classList.remove("is-search-active");
            document.body.style.overflow = "";

            closeAnimationTimeout = setTimeout(() => {
                if (isClosing) {
                    header.classList.remove("menu-open", "menu-closing");
                    megaMenus.forEach((menu) => menu.classList.remove("is-open"));
                    isClosing = false;
                }
            }, 280);
        };

        // Toggle on search button click
        searchToggle.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (isSearchOpen() && !isClosing) {
                closeSearchMenu();
            } else {
                openSearchMenu();
            }
        });

        // When a nav menu trigger is hovered/clicked, remove search active state
        menuTriggers.forEach((trigger) => {
            const onMenuTrigger = () => {
                searchToggle.classList.remove("is-search-active");
            };
            trigger.addEventListener("mouseenter", onMenuTrigger);
            trigger.addEventListener("click", onMenuTrigger);
        });

        // Close search on Escape (only if search is the active menu)
        document.addEventListener("keydown", (e) => {
            if (e.key === "Escape" && isSearchOpen()) {
                closeSearchMenu();
            }
        });

        // Expose for mobile search trigger
        window.__headerSearch = { open: openSearchMenu, close: closeSearchMenu };

        // Mobile search close button
        const mobileSearchClose = document.querySelector("[data-mobile-search-close]");
        if (mobileSearchClose) {
            mobileSearchClose.addEventListener("click", (e) => {
                e.preventDefault();
                closeSearchMenu();
            });
        }

    };

    // Initialize when DOM is ready
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", initSearchButton);
    } else {
        initSearchButton();
    }
})();

// Mobile Search Trigger - Opens header search panel from mobile menu
(() => {
    const initMobileSearchTrigger = () => {
        const mobileTrigger = document.querySelector("[data-mobile-search-trigger]");

        if (!mobileTrigger) return;

        mobileTrigger.addEventListener("click", () => {
            const header = document.querySelector(".site-header");
            const wasMobileMenuOpen = header && header.classList.contains("mobile-menu-open");
            const mobileOverlay = document.querySelector("[data-mobile-menu]");

            // Open search panel first (appears instantly on mobile behind overlay)
            if (window.__headerSearch) {
                window.__headerSearch.open();
            }

            if (wasMobileMenuOpen) {
                // Fade overlay via inline styles (overrides CSS opacity:1 from mobile-menu-open)
                if (mobileOverlay) {
                    mobileOverlay.style.opacity = "0";
                    mobileOverlay.style.visibility = "hidden";
                }

                // After overlay fade (300ms), clean up mobile menu state
                setTimeout(() => {
                    header.classList.remove("mobile-menu-open");
                    header.classList.remove("mobile-submenu-open");
                    // Reset inline styles so CSS classes take over
                    if (mobileOverlay) {
                        mobileOverlay.style.opacity = "";
                        mobileOverlay.style.visibility = "";
                    }
                    // Close all mobile sub-menus
                    document.querySelectorAll("[data-mobile-submenu]").forEach((submenu) => {
                        submenu.classList.remove("is-open");
                        submenu.classList.add("invisible");
                        submenu.style.transform = "translateX(100%)";
                    });
                }, 300);
            }
        });
    };

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", initMobileSearchTrigger);
    } else {
        initMobileSearchTrigger();
    }
})();

// Header Search - Extended Alpine component for inline header search
// Extends the base search() from Common with: tab filtering, clear-on-empty,
// mock data fallbacks, and tab change animation state.
window.headerSearch = (options) => ({
        savedTerms: [],
        savedCookies: [],
        searchValue: "",
        resultsList: [],
        suggestions: [],
        responseList: { results: [] },
        resultsState: false,
        cursorIndex: -1,
        isOpen: false,
        isLoading: false,
        activeTab: "all",
        tabAnimating: false,

        suggestionUri: options.suggestionUri || "#",
        searchUri: options.searchUri || "#",

        init() {
            this.getSavedTerms();
            this.$refs.searchInput.value = "";
            this.cursorIndex = -1;
            this.$watch("activeTab", () => {
                this.tabAnimating = true;
                setTimeout(() => (this.tabAnimating = false), 50);
            });
        },

        autocompleteContainer: {
            ["@keydown.arrow-up"]() {
                if (this.suggestions.length > 0 && this.cursorIndex > 0) {
                    this.cursorIndex--;
                    let oldActive = this.suggestions.find((s) => s.active);
                    if (oldActive) oldActive.active = false;
                    this.suggestions[this.cursorIndex].active = true;
                    this.$refs.searchInput.value = this.resultsList[this.cursorIndex];
                }
            },
            ["@keydown.arrow-down"]() {
                if (this.suggestions.length > 0 && this.cursorIndex < this.suggestions.length - 1) {
                    this.cursorIndex++;
                    let oldActive = this.suggestions.find((s) => s.active);
                    if (oldActive) oldActive.active = false;
                    this.suggestions[this.cursorIndex].active = true;
                    this.$refs.searchInput.value = this.resultsList[this.cursorIndex];
                }
            },
            ["@keyup.enter"]() {
                this.getSearchResults(this.$refs.searchInput.value);
            },
        },

        getSearchResults(searchTerm) {
            this.$refs.searchInput.blur();
            this.resultsState = false;
            this.isOpen = false;
            this.isLoading = true;

            const url = new URL(this.searchUri);
            url.searchParams.set("query", searchTerm);

            fetch(url.toString())
                .then((r) => r.json())
                .then((data) => {
                    this.responseList = data;
                    this.resultsState = true;
                    this.isOpen = false;
                    this.isLoading = false;
                    this.saveSearchTerm(searchTerm);
                })
                .catch((error) => {
                    console.log(error);
                    if (options.mockData) {
                        this.responseList = options.mockData;
                        this.resultsState = true;
                        this.isOpen = false;
                        this.isLoading = false;
                        this.saveSearchTerm(searchTerm);
                    } else {
                        this.isLoading = false;
                    }
                });
        },

        getSuggestions(searchTerm) {
            this.suggestions = [];
            const reg = searchTerm.replace(/\\/g, "");
            const re = new RegExp(reg, "gi");
            this.resultsList.map((el) => {
                this.suggestions.push(el.replace(re, `<strong>$&</strong>`));
            });
        },

        input: {
            ["@input.debounce"](event) {
                this.searchValue = event.target.value;

                if (!this.searchValue.length) {
                    this.suggestions = [];
                    this.resultsState = false;
                    this.responseList = { results: [] };
                    this.activeTab = "all";
                    this.isOpen = false;
                    this.cursorIndex = -1;
                    return;
                }

                const url = new URL(this.suggestionUri);
                url.searchParams.set("term", this.searchValue);

                fetch(url.toString())
                    .then((r) => r.json())
                    .then((data) => {
                        if (data && "completions" in data) {
                            this.resultsList = data.completions;
                            this.getSuggestions(this.searchValue);
                            this.isOpen = true;
                        }
                    })
                    .catch((error) => {
                        console.log(error);
                        if (options.mockSuggestions) {
                            this.resultsList = options.mockSuggestions;
                            this.getSuggestions(this.searchValue);
                            this.isOpen = true;
                        }
                    });
            },
            ["@focus"]() {
                this.isOpen = true;
            },
            ["@click.away"]() {
                this.isOpen = false;
            },
        },

        hintsList: {
            ["@click"](event) {
                const row = event.currentTarget;
                this.searchValue = row.querySelector("span").textContent;
                this.$refs.searchInput.value = this.searchValue;
                this.suggestions = [];
                this.getSearchResults(this.searchValue);
                this.cursorIndex = -1;
                this.isOpen = false;
            },
        },

        showResults: {
            ["@click"]() {
                this.getSearchResults(this.searchValue);
            },
        },

        savedTermsList: {
            ["@click"](event) {
                this.searchValue = event.target.textContent;
                this.$refs.searchInput.value = this.searchValue;
                this.getSearchResults(this.searchValue);
                this.suggestions = [];
            },
        },

        clearSearch: {
            ["@click"]() {
                this.$refs.searchInput.value = "";
                this.suggestions = [];
                this.searchValue = "";
                this.resultsState = false;
                this.responseList = { results: [] };
                this.activeTab = "all";
                this.isOpen = false;
                this.cursorIndex = -1;
                this.$refs.searchInput.focus();
            },
        },

        saveSearchTerm(data) {
            let a = JSON.parse(localStorage.getItem("searchTerms")) || [];
            if (a.includes(data)) return;
            a.unshift(data);
            this.savedTerms.unshift(data);
            localStorage.setItem("searchTerms", JSON.stringify(a));
        },

        getSavedTerms() {
            this.savedTerms = JSON.parse(localStorage.getItem("searchTerms")) || [];
        },
});

// Backend Editor: Sync nav item labels with preview labels + Translate hints
(() => {
    const initLabelSync = () => {
        // Only run on header navigation editor page
        const header = document.querySelector("[data-header-nav-editor]");
        if (!header) return;

        // Set hint text based on Neos interface language
        const hints = header.querySelectorAll(".mega-menu-editor-label__hint");
        hints.forEach((hint) => {
            // Detect Neos interface language from html lang or Neos config
            const lang = document.documentElement.lang || "en";
            // Neos often stores interface language in a data attribute or we can check the URL
            const neosLang =
                document.querySelector("html").getAttribute("data-neos-interface-language") ||
                (window.NeosCMS && window.NeosCMS.interfaceLanguage) ||
                lang.substring(0, 2);

            const hintText = hint.getAttribute(`data-hint-${neosLang}`) || hint.getAttribute("data-hint-en");
            hint.textContent = hintText;
        });

        const navItems = header.querySelectorAll(".nav-items .nav-link");
        const previewLabels = header.querySelectorAll(".mega-menu-editor-label__title");

        if (navItems.length === 0 || previewLabels.length === 0) return;

        // Create a mapping between nav items and preview labels
        // Nav items with submenus correspond to preview labels
        const navItemsWithSubmenu = [];
        navItems.forEach((navItem) => {
            const trigger = navItem.closest("[data-menu-trigger]");
            if (trigger) {
                navItemsWithSubmenu.push(navItem);
            }
        });

        // Watch each nav item for text changes
        navItemsWithSubmenu.forEach((navItem, index) => {
            if (!previewLabels[index]) return;

            const observer = new MutationObserver(() => {
                const newText = navItem.textContent.trim();
                previewLabels[index].textContent = newText;
            });

            observer.observe(navItem, {
                characterData: true,
                childList: true,
                subtree: true,
            });
        });
    };

    // Initialize when DOM is ready
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", initLabelSync);
    } else {
        initLabelSync();
    }
})();
